/**
 * Pitch Deck Generator — Generates a polished, exportable pitch deck
 * from Greenlight Engine analysis results.
 * Part of the Greenlight Engine for 1895.ai.
 */
import { useRef, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Download, Copy, FileText, Star, Users, DollarSign, Target } from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────────────── */

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function scoreHex(score) {
  if (score >= 80) return '#FFD700';
  if (score >= 60) return '#4ECDC4';
  if (score >= 40) return '#FF8C42';
  return '#DC143C';
}

function badgeTierStyle(tier) {
  switch (tier) {
    case 'gold': return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/40' };
    case 'silver': return { bg: 'bg-zinc-400/20', text: 'text-zinc-300', border: 'border-zinc-400/40' };
    case 'bronze': return { bg: 'bg-orange-600/20', text: 'text-orange-400', border: 'border-orange-500/40' };
    default: return { bg: 'bg-zinc-700/20', text: 'text-[var(--text-muted)]', border: 'border-zinc-600/40' };
  }
}

const SUB_SCORE_LABELS = [
  { key: 'writing_craft', label: 'Writing Craft' },
  { key: 'character_arc', label: 'Character Arcs' },
  { key: 'emotional_architecture', label: 'Emotional Architecture' },
  { key: 'narrative_logic', label: 'Narrative Logic' },
  { key: 'commercial_viability', label: 'Commercial Viability' },
  { key: 'audience_breadth', label: 'Audience Breadth' },
  { key: 'producibility', label: 'Producibility' },
  { key: 'originality', label: 'Originality' },
];

/* ─── Plain text generator ────────────────────────────────────────── */

function generatePlainTextDeck(analysis) {
  if (!analysis) return '';

  const {
    greenlight_score, badge, sub_scores, executive_summary,
    characters, commercial,
  } = analysis;

  const summary = executive_summary || {};
  const cv = commercial?.commercial_viability || {};
  const ab = commercial?.audience_breadth || {};
  const prod = commercial?.producibility || {};
  const comps = cv.comparable_films || summary.comparable_films || [];

  const lines = [];
  const hr = '═'.repeat(60);
  const divider = '─'.repeat(60);

  // Title
  lines.push(hr);
  lines.push(`  ${summary.title || 'Untitled Script'}`);
  lines.push(`  Greenlight Score: ${greenlight_score || 0}/100 ${badge?.label ? `[${badge.label}]` : ''}`);
  lines.push(`  Analyzed by 1895.ai`);
  lines.push(hr);
  lines.push('');

  // Logline
  if (summary.one_line_verdict) {
    lines.push('LOGLINE');
    lines.push(divider);
    lines.push(summary.one_line_verdict);
    lines.push('');
  }

  // Synopsis
  if (summary.synopsis) {
    lines.push('SYNOPSIS');
    lines.push(divider);
    lines.push(summary.synopsis);
    lines.push('');
  }

  // Key Metrics
  if (sub_scores) {
    lines.push('KEY METRICS');
    lines.push(divider);
    SUB_SCORE_LABELS.forEach(({ key, label }) => {
      const val = sub_scores[key] ?? '—';
      lines.push(`  ${label.padEnd(26)} ${val}/100`);
    });
    lines.push('');
  }

  // Top Strengths
  if (summary.top_strengths?.length > 0) {
    lines.push('TOP STRENGTHS');
    lines.push(divider);
    summary.top_strengths.slice(0, 3).forEach((s, i) => {
      lines.push(`  ${i + 1}. ${s}`);
    });
    lines.push('');
  }

  // Characters
  const charList = characters?.characters || characters?.cast || [];
  if (charList.length > 0) {
    lines.push('CHARACTER PROFILES');
    lines.push(divider);
    charList.slice(0, 4).forEach((c) => {
      const name = c.name || 'Unknown';
      const role = c.role || c.archetype || '';
      const desc = c.one_liner || c.description || '';
      const cScore = c.score != null ? ` (${c.score}/100)` : '';
      lines.push(`  ${name} — ${role}${cScore}`);
      if (desc) lines.push(`    ${desc}`);
    });
    lines.push('');
  }

  // Audience
  if (ab.gender_appeal || ab.age_appeal) {
    lines.push('AUDIENCE APPEAL');
    lines.push(divider);
    if (ab.gender_appeal) {
      lines.push(`  Male: ${ab.gender_appeal.male || 0}%   Female: ${ab.gender_appeal.female || 0}%`);
    }
    if (ab.age_appeal) {
      Object.entries(ab.age_appeal).forEach(([age, score]) => {
        lines.push(`  ${age.replace('_', '-').padEnd(12)} ${score}%`);
      });
    }
    lines.push('');
  }

  // Market Positioning
  if (summary.market_positioning || comps.length > 0) {
    lines.push('MARKET POSITIONING');
    lines.push(divider);
    if (summary.market_positioning) lines.push(summary.market_positioning);
    if (comps.length > 0) {
      lines.push('');
      lines.push('  Comparable Films:');
      comps.slice(0, 5).forEach((f) => {
        const title = typeof f === 'string' ? f : f.title || f.name || '';
        lines.push(`    - ${title}`);
      });
    }
    lines.push('');
  }

  // Budget & Producibility
  if (prod.budget_tier || prod.estimated_shoot_days) {
    lines.push('BUDGET & PRODUCIBILITY');
    lines.push(divider);
    if (prod.budget_tier) lines.push(`  Budget Tier: ${prod.budget_tier}`);
    if (prod.estimated_shoot_days) lines.push(`  Est. Shoot Days: ${prod.estimated_shoot_days}`);
    if (prod.key_challenges?.length > 0) {
      lines.push('  Key Challenges:');
      prod.key_challenges.forEach((c) => lines.push(`    - ${typeof c === 'string' ? c : c.description || c.challenge || ''}`));
    }
    lines.push('');
  }

  // Verdict
  lines.push('GREENLIGHT VERDICT');
  lines.push(divider);
  lines.push(`  Score: ${greenlight_score || 0}/100`);
  if (badge?.label) lines.push(`  Badge: ${badge.label}`);
  if (summary.one_line_verdict) lines.push(`  ${summary.one_line_verdict}`);
  if (summary.development_notes) {
    lines.push('');
    lines.push('  Development Notes:');
    lines.push(`  ${summary.development_notes}`);
  }
  lines.push('');
  lines.push(hr);
  lines.push('  Generated by 1895.ai Greenlight Engine');
  lines.push(hr);

  return lines.join('\n');
}

/* ─── Slide wrapper ───────────────────────────────────────────────── */

function Slide({ index, title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: 0.05 * index, ease: 'easeOut' }}
      data-slide-index={index}
      className="relative w-full max-w-3xl mx-auto"
    >
      {/* Slide number */}
      <div className="absolute -left-10 top-4 text-[10px] text-[var(--text-muted)] font-mono opacity-50 hidden lg:block">
        {String(index + 1).padStart(2, '0')}
      </div>

      {/* Card */}
      <div
        className="relative overflow-hidden rounded-xl border border-[hsl(var(--border))]
                    bg-[hsl(var(--card))] shadow-lg"
        style={{ minHeight: 280 }}
      >
        {/* Gold left accent */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500/80 via-amber-600/50 to-amber-700/30" />

        {/* Content area */}
        <div className="pl-6 pr-6 py-6 sm:pl-8 sm:pr-8 sm:py-8">
          {/* Slide header */}
          {title && (
            <div className="flex items-center gap-2 mb-5">
              {Icon && <Icon size={16} className="text-amber-500/70" />}
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber-500/70">
                {title}
              </h3>
            </div>
          )}
          {children}
        </div>

        {/* 1895 watermark */}
        <div className="absolute bottom-3 right-4 text-[9px] text-[var(--text-muted)] opacity-30 font-mono">
          1895.ai
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Mini score ring ─────────────────────────────────────────────── */

function MiniRing({ score, size = 44, strokeWidth = 3 }) {
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = scoreHex(score);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="var(--border-subtle, hsl(var(--muted)))" strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[hsl(var(--card-foreground))]">
        {Math.round(score)}
      </span>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────────── */

export default function PitchDeckGenerator({ analysis }) {
  const containerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const SLIDE_COUNT = 10;

  const {
    greenlight_score = 0,
    badge,
    sub_scores,
    executive_summary,
    characters,
    commercial,
  } = analysis || {};

  const summary = executive_summary || {};
  const cv = commercial?.commercial_viability || {};
  const ab = commercial?.audience_breadth || {};
  const prod = commercial?.producibility || {};
  const comps = useMemo(
    () => cv.comparable_films || summary.comparable_films || [],
    [cv.comparable_films, summary.comparable_films]
  );
  const charList = useMemo(
    () => characters?.characters || characters?.cast || [],
    [characters]
  );
  const badgeStyle = badgeTierStyle(badge?.tier);

  /* ── actions ─── */

  const handleExportPDF = useCallback(() => {
    window.print();
  }, []);

  const handleCopy = useCallback(() => {
    const text = generatePlainTextDeck(analysis);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [analysis]);

  const scrollToSlide = useCallback((index) => {
    const el = containerRef.current?.querySelector(`[data-slide-index="${index}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setActiveSlide(index);
    }
  }, []);

  /* ── intersection observer for dot nav ─── */
  const observerRef = useRef(null);
  const setupObserver = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    containerRef.current = node;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.getAttribute('data-slide-index'), 10);
            if (!isNaN(idx)) setActiveSlide(idx);
          }
        });
      },
      { root: null, rootMargin: '-30% 0px -30% 0px', threshold: 0.1 }
    );

    node.querySelectorAll('[data-slide-index]').forEach((el) => {
      observerRef.current.observe(el);
    });
  }, []);

  if (!analysis) return null;

  return (
    <div className="relative">
      {/* ── Toolbar ── */}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-3
                      bg-[hsl(var(--background))]/90 backdrop-blur-md border-b border-[hsl(var(--border))]
                      print:hidden">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-amber-500" />
          <span className="text-sm font-semibold text-[hsl(var(--card-foreground))]">Pitch Deck</span>
          <span className="text-[10px] text-[var(--text-muted)] ml-1">
            {SLIDE_COUNT} slides
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       border border-[hsl(var(--border))] bg-[hsl(var(--card))]
                       text-[hsl(var(--card-foreground))] hover:bg-[hsl(var(--muted))]
                       transition-colors"
          >
            <Copy size={13} />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       bg-amber-500/90 text-[hsl(var(--primary-foreground))] hover:bg-amber-500
                       transition-colors"
          >
            <Download size={13} />
            Export as PDF
          </button>
        </div>
      </div>

      {/* ── Dot navigation ── */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2 print:hidden">
        {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToSlide(i)}
            aria-label={`Slide ${i + 1}`}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeSlide === i
                ? 'bg-amber-500 scale-125'
                : 'bg-[var(--text-muted)] opacity-30 hover:opacity-60'
            }`}
          />
        ))}
      </div>

      {/* ── Slides ── */}
      <div ref={setupObserver} className="flex flex-col gap-8 py-8 px-4 max-w-4xl mx-auto print:gap-0 print:py-0">

        {/* ─ Slide 1: Title ─ */}
        <Slide index={0}>
          <div className="flex flex-col items-center justify-center text-center py-8 min-h-[240px]">
            <h1 className="text-3xl sm:text-4xl font-bold text-[hsl(var(--card-foreground))] mb-4 leading-tight">
              {summary.title || 'Untitled Script'}
            </h1>

            {/* Score badge */}
            <div className="flex items-center gap-3 mb-4">
              <MiniRing score={greenlight_score} size={56} strokeWidth={4} />
              {badge && (
                <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}`}>
                  {badge.label}
                </span>
              )}
            </div>

            <p className="text-xs text-[var(--text-muted)] tracking-wider uppercase">
              Analyzed by 1895.ai Greenlight Engine
            </p>
          </div>
        </Slide>

        {/* ─ Slide 2: Logline ─ */}
        <Slide index={1} title="Logline" icon={FileText}>
          <div className="flex items-center justify-center min-h-[180px]">
            <p className="text-xl sm:text-2xl font-medium text-[hsl(var(--card-foreground))] text-center leading-relaxed italic">
              &ldquo;{summary.one_line_verdict || 'No verdict available.'}&rdquo;
            </p>
          </div>
        </Slide>

        {/* ─ Slide 3: Synopsis ─ */}
        <Slide index={2} title="Synopsis" icon={FileText}>
          <p className="text-sm text-[var(--text-secondary,hsl(var(--muted-foreground)))] leading-relaxed">
            {summary.synopsis || 'No synopsis available.'}
          </p>
        </Slide>

        {/* ─ Slide 4: Key Metrics ─ */}
        <Slide index={3} title="Key Metrics" icon={Target}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {SUB_SCORE_LABELS.map(({ key, label }) => {
              const val = sub_scores?.[key] ?? 0;
              return (
                <div key={key} className="flex flex-col items-center gap-2 p-3 rounded-lg bg-[hsl(var(--background))]/50">
                  <MiniRing score={val} size={50} strokeWidth={3} />
                  <span className="text-[10px] text-[var(--text-muted)] text-center leading-tight">{label}</span>
                </div>
              );
            })}
          </div>
        </Slide>

        {/* ─ Slide 5: Top Strengths ─ */}
        <Slide index={4} title="Top Strengths" icon={Star}>
          <div className="space-y-4">
            {(summary.top_strengths || []).slice(0, 3).map((strength, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <span className="text-xs font-bold text-emerald-400">{i + 1}</span>
                </div>
                <p className="text-sm text-[var(--text-secondary,hsl(var(--muted-foreground)))] pt-1 leading-relaxed">
                  {strength}
                </p>
              </div>
            ))}
            {(!summary.top_strengths || summary.top_strengths.length === 0) && (
              <p className="text-sm text-[var(--text-muted)]">No strengths data available.</p>
            )}
          </div>
        </Slide>

        {/* ─ Slide 6: Character Profiles ─ */}
        <Slide index={5} title="Character Profiles" icon={Users}>
          <div className="space-y-3">
            {charList.slice(0, 4).map((char, i) => {
              const name = char.name || 'Unknown';
              const role = char.role || char.archetype || '';
              const desc = char.one_liner || char.description || '';
              const cScore = char.score;

              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--background))]/50">
                  {/* Character index / protagonist marker */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-[hsl(var(--muted))]/50 text-[var(--text-muted)]'
                  }`}>
                    {i === 0 ? <Star size={14} /> : i + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-[hsl(var(--card-foreground))]">{name}</span>
                      {role && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))]/50 text-[var(--text-muted)] capitalize">
                          {role}
                        </span>
                      )}
                    </div>
                    {desc && (
                      <p className="text-xs text-[var(--text-secondary,hsl(var(--muted-foreground)))]">{desc}</p>
                    )}
                  </div>

                  {cScore != null && <MiniRing score={cScore} size={36} strokeWidth={2.5} />}
                </div>
              );
            })}
            {charList.length === 0 && (
              <p className="text-sm text-[var(--text-muted)]">No character data available.</p>
            )}
          </div>
        </Slide>

        {/* ─ Slide 7: Audience Appeal ─ */}
        <Slide index={6} title="Audience Appeal" icon={Users}>
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Gender gauges */}
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-3 font-semibold">
                Gender Appeal
              </h4>
              <div className="flex gap-6 justify-center">
                {['male', 'female'].map((g) => {
                  const val = ab.gender_appeal?.[g] || 0;
                  return (
                    <div key={g} className="flex flex-col items-center gap-1.5">
                      <MiniRing score={val} size={60} strokeWidth={4} />
                      <span className="text-[10px] text-[var(--text-muted)] capitalize">{g}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Age breakdown */}
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-3 font-semibold">
                Age Breakdown
              </h4>
              <div className="space-y-2">
                {ab.age_appeal && Object.entries(ab.age_appeal).map(([age, score]) => (
                  <div key={age} className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-muted)] w-12 text-right">{age.replace('_', '-')}</span>
                    <div className="flex-1 h-2 rounded-full bg-[hsl(var(--muted))]/50 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-amber-500/80 to-amber-400"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${score}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                      />
                    </div>
                    <span className="text-[10px] font-medium text-[hsl(var(--card-foreground))] w-6">{score}</span>
                  </div>
                ))}
                {!ab.age_appeal && (
                  <p className="text-xs text-[var(--text-muted)]">No age data available.</p>
                )}
              </div>
            </div>
          </div>
        </Slide>

        {/* ─ Slide 8: Market Positioning ─ */}
        <Slide index={7} title="Market Positioning" icon={Target}>
          <div className="space-y-5">
            {summary.market_positioning && (
              <p className="text-sm text-[var(--text-secondary,hsl(var(--muted-foreground)))] leading-relaxed">
                {summary.market_positioning}
              </p>
            )}

            {comps.length > 0 && (
              <div>
                <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-3 font-semibold">
                  Comparable Films
                </h4>
                <div className="flex flex-wrap gap-2">
                  {comps.slice(0, 6).map((film, i) => {
                    const title = typeof film === 'string' ? film : film.title || film.name || '';
                    return (
                      <span
                        key={i}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium
                                   border border-amber-500/20 bg-amber-500/5 text-amber-400/90"
                      >
                        {title}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {!summary.market_positioning && comps.length === 0 && (
              <p className="text-sm text-[var(--text-muted)]">No market positioning data available.</p>
            )}
          </div>
        </Slide>

        {/* ─ Slide 9: Budget & Producibility ─ */}
        <Slide index={8} title="Budget & Producibility" icon={DollarSign}>
          <div className="space-y-5">
            {/* Budget metrics */}
            <div className="flex gap-4 flex-wrap">
              {prod.budget_tier && (
                <div className="px-4 py-3 rounded-lg bg-[hsl(var(--background))]/50 flex-1 min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Budget Tier</div>
                  <div className="text-lg font-bold text-amber-400 capitalize">{prod.budget_tier}</div>
                </div>
              )}
              {prod.estimated_shoot_days && (
                <div className="px-4 py-3 rounded-lg bg-[hsl(var(--background))]/50 flex-1 min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Est. Shoot Days</div>
                  <div className="text-lg font-bold text-[hsl(var(--card-foreground))]">{prod.estimated_shoot_days}</div>
                </div>
              )}
              {prod.score != null && (
                <div className="px-4 py-3 rounded-lg bg-[hsl(var(--background))]/50 flex-1 min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Producibility</div>
                  <div className={`text-lg font-bold ${scoreColor(prod.score)}`}>{prod.score}/100</div>
                </div>
              )}
            </div>

            {/* Key challenges */}
            {prod.key_challenges?.length > 0 && (
              <div>
                <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2 font-semibold">
                  Key Challenges
                </h4>
                <div className="space-y-1.5">
                  {prod.key_challenges.map((c, i) => {
                    const text = typeof c === 'string' ? c : c.description || c.challenge || '';
                    return (
                      <div key={i} className="flex items-start gap-2 text-xs text-[var(--text-secondary,hsl(var(--muted-foreground)))]">
                        <span className="text-amber-500/70 mt-0.5">!</span>
                        <span>{text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!prod.budget_tier && !prod.estimated_shoot_days && (
              <p className="text-sm text-[var(--text-muted)]">No budget data available.</p>
            )}
          </div>
        </Slide>

        {/* ─ Slide 10: Greenlight Verdict ─ */}
        <Slide index={9}>
          <div className="flex flex-col items-center justify-center text-center py-6 min-h-[240px]">
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-500/70 mb-6">
              Greenlight Verdict
            </h3>

            {/* Large score */}
            <div className="relative mb-4">
              <MiniRing score={greenlight_score} size={100} strokeWidth={6} />
              {/* Override the small inner text with a larger one */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-[hsl(var(--card-foreground))]">
                  {Math.round(greenlight_score)}
                </span>
              </div>
            </div>

            {/* Badge */}
            {badge && (
              <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border mb-4 ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}`}>
                {badge.label}
              </span>
            )}

            {/* One-line verdict */}
            {summary.one_line_verdict && (
              <p className="text-base font-medium text-[hsl(var(--card-foreground))] mb-4 max-w-lg">
                {summary.one_line_verdict}
              </p>
            )}

            {/* Development notes */}
            {summary.development_notes && (
              <div className="mt-2 p-4 rounded-lg bg-[hsl(var(--background))]/50 border border-[hsl(var(--border))] max-w-lg text-left">
                <h4 className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2 font-semibold">
                  Development Notes
                </h4>
                <p className="text-xs text-[var(--text-secondary,hsl(var(--muted-foreground)))] leading-relaxed whitespace-pre-line">
                  {summary.development_notes}
                </p>
              </div>
            )}
          </div>
        </Slide>
      </div>

      {/* ── Print styles ── */}
      <style>{`
        @media print {
          body { background: #0f0d0b !important; }
          [data-slide-index] {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
